import { Template, TemplateFilters } from '@/types/Templates'
import api from '@/api'
import cookiesService from '@/services/support/CookiesService'
import config from '@/config'
import { PageRequest, PageResponse } from '@/types/Support'

const LOCALSTORAGE_ITEM_LATEST_TEMPLATES = 'tracking-latest-templates'

class TemplatesService {
  async getTemplates (paging: PageRequest<TemplateFilters>): Promise<PageResponse<Template>> {
    const projectId = cookiesService.get(config.projectIdCookieName)
    const endpoint = `${config.apiFillsUrl}templates`
    const params = {
      'offset': paging.page * paging.size,
      'limit': paging.size,
      'project': projectId,
      'enabled': paging.filters.enabled
    }
    const paramsSort = paging.filters.sort.map(sortItem => `sort=${sortItem}`).join('&')
    const paramsSortTypes = paging.filters.sortType.map(sortTypeItem => `sort-type=${sortTypeItem}`).join('&')
    const separatorSort = paramsSort.length ? '?' : ''
    const separatorSortType = paramsSortTypes.length ? separatorSort.length ? '&' : '?' : ''
    const url = endpoint.concat(separatorSort, paramsSort, separatorSortType, paramsSortTypes)
    const response = await api.get(url, { params })
    const pageResponse = new PageResponse<Template>()
    pageResponse.content = response.data.results
    pageResponse.totalElements = response.data.total
    return pageResponse
  }

  /**
   * Get all the templates based on a configuration
   *
   * @param page configuration to the templates
   * @returns a page reponse with the template values
   */
  async getAllTemplates (page: PageRequest<TemplateFilters>): Promise<PageResponse<Template>> {
    const filters = page.filters
    const params = {
      'sort': page.sort || 'name',
      'sort-type': filters.sortType,
      'limit': page.size || 500,
      'offset': page.page * page.size,
      'domain': filters.domain >= 0 ? filters.domain : null,
      'project': filters.project || null,
      'name': filters.name || null,
      'version': filters.version || null,
      'enable': filters.enabled ?? null,
      'web-enable': filters.webEnabled ?? null,
      'migrated': filters.migrated ?? null,
      'locked': filters.locked ?? null,
      'fields-validation': filters.fieldsValidation ?? null,
      'sub-template': filters.subTemplate ?? null
    }
    const endpoint = `${config.apiFillsUrl}templates`
    const response = await api.get(endpoint, { params })
    return response.data
  }

  /**
   * Gets a template by its id though API FILLS
   * @param templateId to get
   * @returns a template values
   */
  async getTemplate (templateId: number) : Promise<Template> {
    const url = `${config.apiFillsUrl}templates/${templateId}`
    const response = await api.get(url)
    return response.data
  }

  getLatestSelectedTemplatesForTracking (): Template[] {
    const stored = localStorage.getItem(LOCALSTORAGE_ITEM_LATEST_TEMPLATES)
    const projectId = cookiesService.get(config.projectIdCookieName)
    if (stored) {
      const templates = JSON.parse(stored)
      return templates ? templates.filter((it: any) => it.project && it.project.toString() === projectId) : []
    } else {
      return []
    }
  }

  addItemToLatestSelectedTemplatesForTracking (item: Template) {
    const stored = this.getLatestSelectedTemplatesForTracking()

    const existingItem = stored.find(it => it.id === item.id)

    if (!existingItem) {
      stored.unshift(item)
      if (stored.length > 4) {
        stored.pop()
      }
      localStorage.setItem(LOCALSTORAGE_ITEM_LATEST_TEMPLATES, JSON.stringify(stored))
    }
  }
}

export default new TemplatesService()
